'use client';

/**
 * @fileoverview Navbar — public marketing navigation with mega menu.
 *
 * Redesigned from a flat 5-link nav to a structured dropdown navigation
 * that gives enterprise visitors clear paths to product info, developer
 * resources, and company details.
 *
 * Performance notes:
 * - framer-motion removed entirely; dropdowns use CSS transitions.
 * - Scroll handler uses a ref to avoid stale closure re-renders.
 * - Auth actions are lazy-loaded so Clerk doesn't block marketing shell.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import {
  Menu,
  X,
  ChevronDown,
  MousePointerClick,
  Sparkles,
  FileCode2,
  Users,
  LayoutTemplate,
  Zap,
  BookOpen,
  Code2,
  HelpCircle,
  MessageSquare,
  Building2,
  Briefcase,
  Mail,
  ArrowRight,
  History,
  Sun,
  Moon,
} from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { useTheme } from '@/providers/theme-provider';
import { appUrl } from '@/lib/config';

const PublicAuthActions = dynamic(
  () => import('./PublicAuthActions').then((mod) => mod.PublicAuthActions),
  {
    ssr: false,
    loading: () => (
      <div className="flex items-center gap-3">
        <div className="h-9 w-20 animate-pulse rounded-full bg-pb-slate-200/50 dark:bg-pb-slate-800/50" />
        <div className="hidden h-9 w-24 animate-pulse rounded-full bg-pb-slate-200/50 sm:block dark:bg-pb-slate-800/50" />
      </div>
    ),
  }
);

// ── Menu data ─────────────────────────────────────────────────────────────────

type NavItem = {
  icon: React.ReactNode;
  label: string;
  desc: string;
  href: string;
};

type NavGroup = {
  trigger: string;
  sections: { title?: string; items: NavItem[] }[];
  cta?: { label: string; href: string };
};

const navGroups: NavGroup[] = [
  {
    trigger: 'Product',
    sections: [
      {
        title: 'Platform',
        items: [
          { icon: <MousePointerClick size={18} />, label: 'Email Builder', desc: 'Visual email editor', href: appUrl('/editor/new') },
          { icon: <LayoutTemplate size={18} />, label: 'Templates', desc: 'Production-ready starting points', href: '/template' },
          { icon: <Zap size={18} />, label: 'Features', desc: 'Explore all capabilities', href: '/features' },
          { icon: <Sparkles size={18} />, label: 'AI Builder', desc: 'AI-generated copy and layouts', href: '/#feature-ai' },
        ],
      },
      {
        title: 'Performance',
        items: [
          { icon: <Mail size={18} />, label: 'ESP Compatibility', desc: 'Works with any email platform', href: '/#integrations' },
          { icon: <History size={18} />, label: 'Workflow', desc: 'Design, refine, and export', href: '/#workflow' },
        ],
      },
    ],
    cta: { label: 'See all features', href: '/features' },
  },
  {
    trigger: 'Developers',
    sections: [
      {
        items: [
          { icon: <Code2 size={18} />, label: 'API', desc: 'Render emails programmatically', href: '/api-docs' },
          { icon: <FileCode2 size={18} />, label: 'SDK', desc: 'Integration libraries', href: '/api-docs#sdk-render' },
          { icon: <FileCode2 size={18} />, label: 'MJML', desc: 'MJML export controls', href: '/documentation/export/overview' },
          { icon: <BookOpen size={18} />, label: 'Documentation', desc: 'Guides for building emails', href: '/documentation' },
          { icon: <Code2 size={18} />, label: 'API Reference', desc: 'Full API spec', href: '/api-docs#endpoints' },
          { icon: <History size={18} />, label: 'Changelog', desc: 'Latest updates', href: '/changelog' },
        ],
      },
    ],
    cta: { label: 'Explore developer guide', href: '/documentation/developers/api' },
  },
  {
    trigger: 'Solutions',
    sections: [
      {
        items: [
          { icon: <Code2 size={18} />, label: 'Developers', desc: 'For engineering teams', href: '/solutions/developers' },
          { icon: <Zap size={18} />, label: 'Marketing Teams', desc: 'For growth and CRM', href: '/solutions/marketing' },
          { icon: <Users size={18} />, label: 'Agencies', desc: 'For client projects', href: '/solutions/agencies' },
          { icon: <Building2 size={18} />, label: 'SaaS Companies', desc: 'For product emails', href: '/solutions/saas' },
          { icon: <Briefcase size={18} />, label: 'Enterprise', desc: 'For scale and security', href: '/solutions/enterprise' },
        ],
      },
    ],
    cta: { label: 'Talk to sales', href: '/talktosales' },
  },
  {
    trigger: 'Resources',
    sections: [
      {
        items: [
          { icon: <BookOpen size={18} />, label: 'Documentation', desc: 'Guides & references', href: '/documentation' },
          { icon: <LayoutTemplate size={18} />, label: 'Templates', desc: 'Free email templates', href: '/template' },
          { icon: <Zap size={18} />, label: 'Guides', desc: 'How-to guides & best practices', href: '/resources' },
          { icon: <MessageSquare size={18} />, label: 'Community', desc: 'Join the discussion', href: '/community' },
          { icon: <HelpCircle size={18} />, label: 'FAQ', desc: 'Common questions & answers', href: '/#faq' },
        ],
      },
    ],
    cta: { label: 'Browse resource hub', href: '/resources' },
  },
];

// ── Component ─────────────────────────────────────────────────────────────────

export const Navbar = () => {
  const { theme, setTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const dropdownTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Body scroll lock for mobile menu
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  // Dropdown handlers with hover intent delay
  const handleDropdownEnter = useCallback((trigger: string) => {
    if (dropdownTimeoutRef.current) clearTimeout(dropdownTimeoutRef.current);
    setOpenDropdown(trigger);
  }, []);

  const handleDropdownLeave = useCallback(() => {
    dropdownTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 150);
  }, []);

  return (
    <nav
      className="relative z-40 w-full transition-all duration-200"
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="mx-auto flex h-16 max-w-7xl 2xl:max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* LOGO */}
        <Link href="/" prefetch={false} className="z-50 flex items-center gap-2 shrink-0" aria-label="PostBrix home">
          <Logo size={38} />
        </Link>

        {/* DESKTOP NAV */}
        <div className="hidden items-center gap-1 lg:flex">
          {navGroups.map((group) => (
            <div
              key={group.trigger}
              className="relative"
              onMouseEnter={() => handleDropdownEnter(group.trigger)}
              onMouseLeave={handleDropdownLeave}
            >
              {/* Trigger button */}
              <button
                className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[14px] font-semibold text-pb-slate-700 transition-colors hover:bg-pb-slate-100/70 hover:text-pb-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-blue-500 focus-visible:ring-offset-2 dark:text-pb-slate-200 dark:hover:bg-pb-white/[0.08] dark:hover:text-pb-white"
                aria-expanded={openDropdown === group.trigger}
                aria-haspopup="true"
                onClick={() => setOpenDropdown(openDropdown === group.trigger ? null : group.trigger)}
              >
                {group.trigger}
                <ChevronDown
                  size={14}
                  className={`text-pb-slate-400 transition-transform duration-200 ${
                    openDropdown === group.trigger ? 'rotate-180 text-pb-blue-600 dark:text-pb-blue-400' : ''
                  }`}
                />
              </button>

              {/* Dropdown panel */}
              <div
                className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-2 transition-all duration-200 ${
                  openDropdown === group.trigger
                    ? 'pointer-events-auto translate-y-0 opacity-100'
                    : 'pointer-events-none translate-y-1 opacity-0'
                }`}
                onMouseEnter={() => handleDropdownEnter(group.trigger)}
                onMouseLeave={handleDropdownLeave}
              >
                <div className="min-w-[340px] overflow-hidden rounded-2xl border border-pb-slate-200/90 bg-pb-white/98 p-2.5 shadow-2xl shadow-pb-slate-900/10 backdrop-blur-xl dark:border-pb-white/[0.1] dark:bg-[#0b1329]/98 dark:shadow-black/50">
                  {group.sections.map((section, si) => (
                    <div key={si}>
                      {section.title && (
                        <p className="px-3 pt-2 pb-1 text-[12px] font-bold tracking-[0.12em] uppercase text-pb-slate-400 dark:text-pb-slate-500">
                          {section.title}
                        </p>
                      )}
                      {section.items.map((item) => (
                        <Link
                          key={`${item.label}-${item.href}`}
                          href={item.href}
                          prefetch={false}
                          onClick={() => setOpenDropdown(null)}
                          className="group flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-pb-slate-100/70 dark:hover:bg-pb-white/[0.06]"
                        >
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pb-slate-100/90 text-pb-slate-600 transition-colors group-hover:bg-pb-blue-50 group-hover:text-pb-blue-600 dark:bg-pb-white/[0.06] dark:text-pb-slate-300 dark:group-hover:bg-pb-blue-500/15 dark:group-hover:text-pb-blue-400">
                            {item.icon}
                          </span>
                          <div className="min-w-0">
                            <p className="text-[14px] font-semibold text-pb-slate-900 dark:text-pb-white">
                              {item.label}
                            </p>
                            <p className="text-[12.5px] leading-snug text-pb-slate-500 dark:text-pb-slate-400">
                              {item.desc}
                            </p>
                          </div>
                        </Link>
                      ))}
                      {si < group.sections.length - 1 && (
                        <div className="my-1.5 mx-2 h-px bg-pb-slate-100 dark:bg-pb-white/[0.06]" />
                      )}
                    </div>
                  ))}
                  {group.cta && (
                    <>
                      <div className="my-1.5 mx-2 h-px bg-pb-slate-100 dark:bg-pb-white/[0.06]" />
                      <Link
                        href={group.cta.href}
                        prefetch={false}
                        onClick={() => setOpenDropdown(null)}
                        className="flex items-center gap-1.5 rounded-xl px-3 py-2 text-[13.5px] font-semibold text-pb-blue-600 transition-colors hover:bg-pb-blue-50/70 dark:text-pb-blue-400 dark:hover:bg-pb-blue-500/10"
                      >
                        {group.cta.label}
                        <ArrowRight size={13} />
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Direct pricing link (always visible) */}
          <Link
            href="/pricing"
            prefetch={false}
            className="rounded-lg px-3.5 py-2 text-[14px] font-semibold text-pb-slate-700 transition-colors hover:bg-pb-slate-100/70 hover:text-pb-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-blue-500 focus-visible:ring-offset-2 dark:text-pb-slate-200 dark:hover:bg-pb-white/[0.08] dark:hover:text-pb-white"
          >
            Pricing
          </Link>
        </div>

        {/* AUTH ACTIONS + THEME */}
        <div className="hidden items-center gap-3 lg:flex">
          <PublicAuthActions variant="desktop" />
          <ThemeToggle />
        </div>

        {/* MOBILE ACTIONS: THEME TOGGLE + HAMBURGER */}
        <div className="flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button
            className="relative z-50 flex h-10 w-10 items-center justify-center rounded-xl border border-pb-slate-200/90 bg-pb-white text-pb-slate-700 shadow-xs transition-all active:scale-90 hover:bg-pb-slate-50 hover:text-pb-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-blue-500 dark:border-pb-white/[0.1] dark:bg-pb-white/[0.04] dark:text-pb-slate-200 dark:hover:bg-pb-white/[0.08] cursor-pointer"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* ── MOBILE DRAWER ──────────────────────────────────────── */}
      {typeof document !== 'undefined' &&
        mobileMenuOpen &&
        document.body &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              id="mobile-menu-backdrop"
              className={`fixed inset-0 z-[100] bg-pb-slate-900/40 backdrop-blur-sm lg:hidden transition-opacity duration-200 ${
                mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
              onClick={closeMobileMenu}
              aria-hidden="true"
            />
            {/* Drawer */}
            <div
              id="mobile-menu"
              role="dialog"
              aria-modal="true"
              aria-labelledby="mobile-menu-title"
              className={`bg-pb-white dark:bg-pb-navy fixed inset-y-0 right-0 z-[101] flex w-[85%] max-w-sm flex-col border-l border-outline-variant shadow-2xl lg:hidden transition-transform duration-300 ease-in-out ${
                mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
            >
              <div className="flex items-center justify-between border-b border-outline-variant p-5">
                <span id="mobile-menu-title" className="sr-only">Mobile navigation</span>
                <Logo size={32} />
                <button
                  className="rounded-lg border border-outline-variant bg-pb-slate-50 dark:bg-pb-white/[0.04] p-2 text-on-surface-disabled"
                  onClick={closeMobileMenu}
                  aria-label="Close menu"
                >
                  <X size={18} />
                </button>
              </div>
      
              <div className="flex-1 space-y-1 overflow-y-auto p-4">
                <nav aria-label="Mobile navigation links">
                  {navGroups.map((group) => (
                    <div key={group.trigger} className="mb-4">
                      <p className="px-3 py-1.5 text-[12px] font-bold tracking-[0.12em] uppercase text-pb-slate-400 dark:text-pb-slate-500">
                        {group.trigger}
                      </p>
                      {group.sections.map((section) =>
                        section.items.map((item) => (
                          <Link
                            key={`${item.label}-${item.href}`}
                            href={item.href}
                            prefetch={false}
                            onClick={closeMobileMenu}
                            className="flex items-center gap-3.5 rounded-xl px-3.5 py-3 text-[15px] font-semibold text-pb-slate-800 transition-colors hover:bg-pb-slate-100/80 dark:text-pb-slate-200 dark:hover:bg-pb-white/[0.06]"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pb-slate-100 dark:bg-pb-white/[0.06] text-pb-slate-600 dark:text-pb-slate-300">
                              {item.icon}
                            </span>
                            {item.label}
                          </Link>
                        ))
                      )}
                    </div>
                  ))}
                  <Link
                    href="/pricing"
                    prefetch={false}
                    onClick={closeMobileMenu}
                    className="flex items-center gap-3.5 rounded-xl px-3.5 py-3 text-[15px] font-semibold text-pb-slate-800 transition-colors hover:bg-pb-slate-100/80 dark:text-pb-slate-200 dark:hover:bg-pb-white/[0.06]"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-pb-slate-100 dark:bg-pb-white/[0.06] text-pb-slate-600 dark:text-pb-slate-300">
                      <Zap size={16} />
                    </span>
                    Pricing
                  </Link>
                </nav>
      
                {/* Mobile Appearance Segmented Switcher */}
                <div className="mt-4 rounded-2xl border border-outline-variant bg-pb-slate-50/80 p-3 dark:border-pb-white/[0.08] dark:bg-pb-white/[0.03]">
                  <div className="mb-2.5 flex items-center justify-between px-1">
                    <span className="text-[11px] font-bold tracking-[0.1em] uppercase text-pb-slate-600 dark:text-pb-slate-300">
                      Appearance
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-pb-slate-600 dark:text-pb-slate-300">
                      <span className={`inline-block h-2 w-2 rounded-full ${theme === 'dark' ? 'bg-blue-400' : 'bg-amber-500'}`} />
                      <span className="capitalize">{theme} Mode</span>
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 rounded-xl bg-pb-slate-200/60 p-1 dark:bg-pb-slate-900/80">
                    <button
                      type="button"
                      onClick={() => setTheme('light')}
                      className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold transition-all active:scale-95 cursor-pointer ${
                        theme === 'light'
                          ? 'bg-pb-white text-pb-slate-900 shadow-sm shadow-pb-slate-900/10'
                          : 'text-pb-slate-600 hover:text-pb-slate-900 dark:text-pb-slate-400 dark:hover:text-pb-white'
                      }`}
                      aria-label="Set light theme"
                    >
                      <Sun size={16} className={theme === 'light' ? 'text-amber-500' : 'text-pb-slate-400'} />
                      <span>Light</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setTheme('dark')}
                      className={`flex items-center justify-center gap-2 rounded-lg py-2.5 text-xs font-bold transition-all active:scale-95 cursor-pointer ${
                        theme === 'dark'
                          ? 'bg-pb-blue-600 text-pb-white shadow-sm shadow-pb-blue-600/30 dark:bg-pb-slate-800 dark:text-pb-white'
                          : 'text-pb-slate-600 hover:text-pb-slate-900 dark:text-pb-slate-400 dark:hover:text-pb-white'
                      }`}
                      aria-label="Set dark theme"
                    >
                      <Moon size={16} className={theme === 'dark' ? 'text-blue-400' : 'text-pb-slate-400'} />
                      <span>Dark</span>
                    </button>
                  </div>
                </div>
      
                <div className="mt-4 space-y-3 border-t border-outline-variant pt-4">
                  <PublicAuthActions variant="mobile" />
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </nav>
  );
};
